import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import Navigation from '../components/Navigation';
import HomeBanner from '../components/HomeBanner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Calendar, Clock, FileText, CheckCircle2, XCircle, AlertCircle, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Currency symbols mapping
const currencySymbols = {
  'INR': '₹',
  'USD': '$',
  'EUR': '€',
  'GBP': '£',
  'AED': 'د.إ',
  'SGD': 'S$',
  'AUD': 'A$',
  'CAD': 'C$'
};

// Helper function to format salary value with currency
const formatSalary = (value, currency) => {
  if (!value || value === 'NA' || value === 'na' || value === '' || value === '0') return null;
  const symbol = currencySymbols[currency] || currency || '';
  // Format number with commas
  const num = parseFloat(value.replace(/,/g, ''));
  if (isNaN(num)) return null;
  return `${symbol}${num.toLocaleString('en-IN')}`;
};

const EmployeeDashboard = ({ user, setUser }) => {
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [pendingApprovals, setPendingApprovals] = useState({ attendance: 0, leaves: 0 });
  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [balanceRes, attendanceRes, leavesRes, detailsRes] = await Promise.all([
        axios.get(`${API}/leaves/balance`, config),
        axios.get(`${API}/attendance`, config),
        axios.get(`${API}/leaves`, config),
        axios.get(`${API}/users/${user.id}/details`, config),
      ]);

      setLeaveBalance(balanceRes.data);
      setEmployeeDetails(detailsRes.data);

      const pendingAttendance = attendanceRes.data.filter(a => a.approval_status === 'pending').length;
      const pendingLeaves = leavesRes.data.filter(l => l.approval_status === 'pending').length;
      setPendingApprovals({ attendance: pendingAttendance, leaves: pendingLeaves });
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50" data-testid="employee-dashboard">
      <Header user={user} setUser={setUser} />
      <Navigation user={user} />
      <HomeBanner />
      
      <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-screen-2xl mx-auto w-full">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2" data-testid="welcome-message">
            Welcome back, {user.name}!
          </h1>
          <p className="text-gray-600 text-lg">Here's your overview for today</p>
        </div>

        {loading ? (
          <div className="text-center py-12">Loading...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Annual Leave */}
            <Card className="border-l-4 border-l-blue-500 shadow-md hover:shadow-lg transition-shadow" data-testid="al-balance-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Annual Leave
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-blue-600" data-testid="al-available">
                  {leaveBalance?.AL?.available || 0}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  of {leaveBalance?.AL?.total || 0} days available
                </p>
              </CardContent>
            </Card>

            {/* Organization Holiday */}
            <Card className="border-l-4 border-l-green-500 shadow-md hover:shadow-lg transition-shadow" data-testid="oh-balance-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Organization Holiday
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-green-600" data-testid="oh-available">
                  {leaveBalance?.OH?.available || 0}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  of {leaveBalance?.OH?.total || 0} days available
                </p>
              </CardContent>
            </Card>

            {/* Restricted Holiday */}
            <Card className="border-l-4 border-l-purple-500 shadow-md hover:shadow-lg transition-shadow" data-testid="rh-balance-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Restricted Holiday
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-purple-600" data-testid="rh-available">
                  {leaveBalance?.RH?.available || 0}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  of {leaveBalance?.RH?.total || 0} days available
                </p>
              </CardContent>
            </Card>

            {/* Pending Approvals */}
            <Card className="border-l-4 border-l-orange-500 shadow-md hover:shadow-lg transition-shadow" data-testid="pending-approvals-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Pending Approvals
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Attendance</span>
                    <span className="text-lg font-semibold text-orange-600" data-testid="pending-attendance">
                      {pendingApprovals.attendance}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Leaves</span>
                    <span className="text-lg font-semibold text-orange-600" data-testid="pending-leaves">
                      {pendingApprovals.leaves}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Employee Details Boxes */}
        {employeeDetails && (
          <div className="mt-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">My Information</h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Personal Details Box */}
              <Card className="shadow-md" data-testid="personal-details-card">
                <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    Personal Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  {/* Profile Photo */}
                  {employeeDetails.profile_photo && (
                    <div className="flex justify-center mb-6">
                      <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-blue-200 shadow-lg">
                        <img 
                          src={employeeDetails.profile_photo} 
                          alt={`${employeeDetails.name}'s profile`}
                          className="w-full h-full object-cover"
                          data-testid="employee-profile-photo"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    </div>
                  )}
                  <div className="space-y-3">
                    {employeeDetails.gender && (
                      <div className="flex justify-between border-b pb-2">
                        <span className="text-gray-600">Gender:</span>
                        <span className="font-semibold">{employeeDetails.gender}</span>
                      </div>
                    )}
                    {employeeDetails.dob && (
                      <div className="flex justify-between border-b pb-2">
                        <span className="text-gray-600">Date of Birth:</span>
                        <span className="font-semibold">{employeeDetails.dob}</span>
                      </div>
                    )}
                    {employeeDetails.marital_status && (
                      <div className="flex justify-between border-b pb-2">
                        <span className="text-gray-600">Marital Status:</span>
                        <span className="font-semibold">{employeeDetails.marital_status}</span>
                      </div>
                    )}
                    {employeeDetails.nationality && (
                      <div className="flex justify-between border-b pb-2">
                        <span className="text-gray-600">Nationality:</span>
                        <span className="font-semibold">{employeeDetails.nationality}</span>
                      </div>
                    )}
                    {employeeDetails.contact_number && (
                      <div className="flex justify-between border-b pb-2">
                        <span className="text-gray-600">Contact Number:</span>
                        <span className="font-semibold">{employeeDetails.contact_number}</span>
                      </div>
                    )}
                    {employeeDetails.alternate_number && (
                      <div className="flex justify-between border-b pb-2">
                        <span className="text-gray-600">Alternate Number:</span>
                        <span className="font-semibold">{employeeDetails.alternate_number}</span>
                      </div>
                    )}
                    {employeeDetails.email && (
                      <div className="flex justify-between border-b pb-2">
                        <span className="text-gray-600">Email:</span>
                        <span className="font-semibold text-sm">{employeeDetails.email}</span>
                      </div>
                    )}
                    {employeeDetails.alternate_email && (
                      <div className="flex justify-between border-b pb-2">
                        <span className="text-gray-600">Alternate Email:</span>
                        <span className="font-semibold text-sm">{employeeDetails.alternate_email}</span>
                      </div>
                    )}
                    {employeeDetails.present_address && (
                      <div className="border-b pb-2">
                        <span className="text-gray-600 block mb-1">Present Address:</span>
                        <span className="font-semibold text-sm">{employeeDetails.present_address}</span>
                      </div>
                    )}
                    {employeeDetails.education && (
                      <div className="flex justify-between border-b pb-2">
                        <span className="text-gray-600">Education:</span>
                        <span className="font-semibold">{employeeDetails.education}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Employment Details Box */}
              <Card className="shadow-md" data-testid="employment-details-card">
                <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    Employment Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="space-y-3">
                    <div className="flex justify-between border-b pb-2">
                      <span className="text-gray-600">Employee ID:</span>
                      <span className="font-semibold">{employeeDetails.employee_id}</span>
                    </div>
                    {employeeDetails.department && (
                      <div className="flex justify-between border-b pb-2">
                        <span className="text-gray-600">Department:</span>
                        <span className="font-semibold">{employeeDetails.department}</span>
                      </div>
                    )}
                    {employeeDetails.designation && (
                      <div className="flex justify-between border-b pb-2">
                        <span className="text-gray-600">Designation:</span>
                        <span className="font-semibold">{employeeDetails.designation}</span>
                      </div>
                    )}
                    {employeeDetails.employee_type && (
                      <div className="flex justify-between border-b pb-2">
                        <span className="text-gray-600">Employee Type:</span>
                        <span className="font-semibold">{employeeDetails.employee_type}</span>
                      </div>
                    )}
                    {employeeDetails.level && (
                      <div className="flex justify-between border-b pb-2">
                        <span className="text-gray-600">Level:</span>
                        <span className="font-semibold">{employeeDetails.level}</span>
                      </div>
                    )}
                    <div className="flex justify-between border-b pb-2">
                      <span className="text-gray-600">Date of Joining:</span>
                      <span className="font-semibold">{employeeDetails.date_of_joining}</span>
                    </div>
                    {employeeDetails.actual_date_of_joining && (
                      <div className="flex justify-between border-b pb-2">
                        <span className="text-gray-600">Actual DOJ:</span>
                        <span className="font-semibold">{employeeDetails.actual_date_of_joining}</span>
                      </div>
                    )}
                    {employeeDetails.reporting_manager && (
                      <div className="flex justify-between border-b pb-2">
                        <span className="text-gray-600">Reporting Manager:</span>
                        <span className="font-semibold">{employeeDetails.reporting_manager}</span>
                      </div>
                    )}
                    {employeeDetails.hr_partner && (
                      <div className="flex justify-between border-b pb-2">
                        <span className="text-gray-600">HR Partner:</span>
                        <span className="font-semibold">{employeeDetails.hr_partner}</span>
                      </div>
                    )}
                    {employeeDetails.total_work_experience && (
                      <div className="flex justify-between border-b pb-2">
                        <span className="text-gray-600">Total Experience:</span>
                        <span className="font-semibold">{employeeDetails.total_work_experience}</span>
                      </div>
                    )}
                    {employeeDetails.work_experience_as_employee && (
                      <div className="flex justify-between border-b pb-2">
                        <span className="text-gray-600">Experience Here:</span>
                        <span className="font-semibold">{employeeDetails.work_experience_as_employee}</span>
                      </div>
                    )}
                    {employeeDetails.project_name && (
                      <div className="flex justify-between border-b pb-2">
                        <span className="text-gray-600">Project:</span>
                        <span className="font-semibold">{employeeDetails.project_name}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Salary Structure Box - Only show if any salary field has a value */}
              {(() => {
                const currency = employeeDetails.salary_currency;
                const salaryFields = [
                  { key: 'fixed_monthly_salary', label: 'Fixed Monthly Salary' },
                  { key: 'internet_phone_allowance', label: 'Internet & Phone Allowance' },
                  { key: 'monthly_performance_bonus', label: 'Monthly Performance Bonus' },
                  { key: 'byod_allowance', label: 'BYOD Allowance' },
                  { key: 'half_yearly_retention_bonus', label: 'Half Yearly Retention Bonus' },
                  { key: 'managerial_allowance', label: 'Managerial Allowance' },
                  { key: 'provident_fund', label: 'Provident Fund' },
                  { key: 'medical_allowance', label: 'Medical Allowance' },
                  { key: 'gratuity', label: 'Gratuity' },
                  { key: 'hra', label: 'HRA' },
                  { key: 'ctc_yearly', label: 'CTC (Yearly)' }
                ];
                
                // Filter to only show fields with valid numeric values
                const validFields = salaryFields.filter(field => {
                  const value = employeeDetails[field.key];
                  return formatSalary(value, currency) !== null;
                });
                
                // Only render the card if there are salary fields to show
                if (validFields.length === 0) return null;
                
                return (
                  <Card className="shadow-md" data-testid="salary-structure-card">
                    <CardHeader className="bg-gradient-to-r from-amber-50 to-yellow-50">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center">
                          <DollarSign className="w-5 h-5 text-white" />
                        </div>
                        Salary Structure
                        {currency && (
                          <span className="ml-auto text-sm font-normal text-gray-500 bg-amber-100 px-2 py-1 rounded">
                            {currency}
                          </span>
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6">
                      <div className="space-y-3">
                        {validFields.map(field => (
                          <div key={field.key} className="flex justify-between border-b pb-2">
                            <span className="text-gray-600">{field.label}:</span>
                            <span className="font-semibold text-green-700">
                              {formatSalary(employeeDetails[field.key], currency)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })()}
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="mt-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="cursor-pointer hover:shadow-lg transition-shadow bg-gradient-to-br from-blue-50 to-blue-100 border-0" onClick={() => window.location.href = '/attendance'} data-testid="quick-action-attendance">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Mark Attendance</h3>
                    <p className="text-sm text-gray-600">Submit today's attendance</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:shadow-lg transition-shadow bg-gradient-to-br from-green-50 to-green-100 border-0" onClick={() => window.location.href = '/leaves'} data-testid="quick-action-leave">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Apply for Leave</h3>
                    <p className="text-sm text-gray-600">Submit leave application</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:shadow-lg transition-shadow bg-gradient-to-br from-purple-50 to-purple-100 border-0" onClick={() => window.location.href = '/holidays'} data-testid="quick-action-holidays">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">View Holidays</h3>
                    <p className="text-sm text-gray-600">Check holiday calendar</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeDashboard;
